import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchCurrentUser } from '@/lib/api'
import type { CurrentUser } from '@/auth/permissions'
import { currentLocale, setLocale as persistLocale, translate, type Locale } from '@/i18n'
import { applyTheme, themePreference, watchSystemTheme, type ThemePreference } from './theme'

/**
 * Общее состояние приложения: пользователь, язык, тема (ТЗ §5.1).
 *
 * Права загружаются один раз при входе и живут в кеше React Query. Перезапрашивать их
 * на каждом экране значило бы, что меню моргает при каждом переходе, — а меняются они
 * ровно тогда, когда администратор трогает роли, то есть почти никогда.
 */

interface AppState {
  user: CurrentUser | null
  isLoadingUser: boolean
  userError: Error | null
  locale: Locale
  setLocale: (locale: Locale) => void
  theme: ThemePreference
  setTheme: (theme: ThemePreference) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const AppStateContext = createContext<AppState | null>(null)

export function AppProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(currentLocale)
  const [theme, setThemeState] = useState<ThemePreference>(themePreference)

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['me'],
    enabled: !['/callback', '/silent-renew'].includes(window.location.pathname),
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    // Права меняются при правке ролей администратором, то есть почти никогда.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const setLocale = useCallback((next: Locale) => {
    persistLocale(next)
    setLocaleState(next)
  }, [])

  const setTheme = useCallback((next: ThemePreference) => {
    applyTheme(next)
    setThemeState(next)
  }, [])

  // Подписка нужна только при выборе «как в системе»: при явном выборе системные
  // изменения игнорируются, иначе ОС перезаписывала бы решение пользователя.
  useEffect(() => watchSystemTheme(() => applyTheme('system')), [])

  const value = useMemo<AppState>(
    () => ({
      user: user ?? null,
      isLoadingUser: isLoading,
      userError: (error as Error) ?? null,
      locale,
      setLocale,
      theme,
      setTheme,
      t: (key, params) => translate(key, locale, params),
    }),
    [user, isLoading, error, locale, setLocale, theme, setTheme],
  )

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
}

export function useApp(): AppState {
  const state = useContext(AppStateContext)
  if (!state) {
    // Провайдер обязателен: без него `t` и `user` были бы undefined, и падало бы
    // это в случайном компоненте далеко от причины.
    throw new Error('useApp вызван вне AppProvider')
  }
  return state
}
